import { useRef, useCallback, useState, useEffect } from 'react';
import Supercluster from 'supercluster';
import { Marker } from 'react-map-gl/mapbox';
import type { ClusterFeature, PointFeature } from 'supercluster';
import { throttle } from 'lodash';

import { useMapboxStore } from '../store';
import Place from './place';
import { PlaceProps } from '../../mapbox-common/types';
import { Typography } from '@mui/material';
import { Cluster } from '../../mapbox-common/styles';

type TClusterProperties = PlaceProps & {
  cluster?: boolean;
  cluster_id?: number;
  point_count?: number;
  point_count_abbreviated?: string | number;
};

type TCluster = PointFeature<TClusterProperties> | ClusterFeature<TClusterProperties>;

const convertPointsToGeoJSON = (points: PlaceProps[]) =>
  points.map((p) => ({
    type: 'Feature' as const,
    properties: { ...p },
    geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
  }));

export default function Places() {
  const { currentPlaces, mapRef } = useMapboxStore();
  const [clusters, setClusters] = useState<TCluster[]>([]);
  const superclusterRef = useRef(
    new Supercluster({
      radius: 30,
      maxZoom: 18,
    }),
  );

  const updateClusters = useCallback(
    throttle(() => {
      if (!mapRef?.getMap()) return;
      const map = mapRef.getMap();
      const rawBounds = map.getBounds()?.toArray().flat() || [-180, -90, 180, 90];
      const bounds = [
        rawBounds[0] - 0.01,
        rawBounds[1] - 0.01,
        rawBounds[2] + 0.01,
        rawBounds[3] + 0.01,
      ];
      const zoom = Math.floor(map.getZoom() || 0);
      const clusters = superclusterRef.current.getClusters(
        bounds as [number, number, number, number],
        zoom,
      );
      setClusters(clusters as TCluster[]);
    }, 100),
    [mapRef],
  );

  useEffect(() => {
    if (!mapRef?.getMap()) return;
    const map = mapRef.getMap();
    superclusterRef.current.load(convertPointsToGeoJSON(currentPlaces));
    updateClusters();
    map.on('zoom', updateClusters);
    return () => {
      map.off('zoom', updateClusters);
      updateClusters.cancel(); // Clean up throttled function
    };
  }, [currentPlaces, mapRef, updateClusters]);

  const handleClusterZoom = useCallback(
    (cluster: any) => () => {
      mapRef?.flyTo({
        center: cluster.geometry.coordinates,
        zoom: cluster.properties.point_count > 10 ? 14 : 16,
        duration: 500,
      });
    },
    [mapRef],
  );

  return (
    <>
      {clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;

        if (isCluster) {
          return (
            <Marker key={`cluster-${cluster.id}`} longitude={longitude} latitude={latitude}>
              <Cluster onClick={handleClusterZoom(cluster)}>
                <Typography variant="h5" color="white" fontWeight={600}>
                  {pointCount}
                </Typography>
              </Cluster>
            </Marker>
          );
        }

        return <Place {...cluster.properties} key={cluster.properties.fsq_id} />;
      })}
    </>
  );
}
